import React, { useEffect, useState } from 'react';
import { WhatsAppButton } from '../../components/WhatsApp.jsx';
import { Link, useParams } from 'react-router-dom';
import { api } from '../../api.js';
import { useI18n, usePageTitle } from '../../i18n/index.jsx';
import { useAuth } from '../../auth.jsx';
import { formatPrice, formatQuantity, formatDate, categoryName, placeName } from '../../format.js';
import ContactSellerForm from './ContactSellerForm.jsx';
import ProductFacts from '../../components/ProductFacts.jsx';
import { formatAmount, incotermLabel } from '../../productOptions.js';
import NotFound from '../site/NotFound.jsx';
import StartChatButton from '../../chat/StartChatButton.jsx';
import { Avatar } from '../workspace/ws.jsx';
import TutorialSlot from '../../tutorials/TutorialSlot.jsx';

// YouTube / Vimeo links play on the page; other links open in a new tab.
function embedUrl(url) {
  const u = String(url || '');
  const yt = u.match(/^https:\/\/(?:www\.|m\.)?(?:youtube\.com\/(?:watch\?(?:.*&)?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{11})/);
  if (yt) return `https://www.youtube-nocookie.com/embed/${yt[1]}`;
  const vm = u.match(/^https:\/\/(?:www\.)?vimeo\.com\/(\d+)/);
  return vm ? `https://player.vimeo.com/video/${vm[1]}` : null;
}

export default function ListingDetail() {
  const { id } = useParams();
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [listing, setListing] = useState(null);
  const [missing, setMissing] = useState(false);
  const [active, setActive] = useState(0);
  usePageTitle(null);

  useEffect(() => {
    let alive = true;
    setListing(null); setMissing(false); setActive(0);
    api(`/marketplace/listings/${id}`).then((l) => alive && setListing(l)).catch(() => alive && setMissing(true));
    return () => { alive = false; };
  }, [id]);
  useEffect(() => { if (listing) document.title = `${listing.title} | ${t('brand.name')}`; }, [listing, t]);

  if (missing) return <NotFound />;
  if (!listing) return <div className="page-loading">{t('common.loading')}</div>;

  const qty = formatQuantity(listing, lang);
  const place = placeName(listing.seller, t);
  const photos = listing.photos;
  const isOwn = user && user.slug === listing.seller.slug;

  return (
    <div className="container page">
      <p><Link to="/marketplace" className="back-link">← {t('listing.back')}</Link></p>
      {listing.categoryPath?.length > 0 && (
        <nav className="listing-crumbs" aria-label={t('wizard.breadcrumb')}>
          {listing.categoryPath.map((c, i) => (
            <React.Fragment key={c.slug}>
              {i > 0 && <span aria-hidden="true">›</span>}
              <Link to={`/marketplace?category=${encodeURIComponent(c.slug)}`}>{lang === 'en' ? c.nameEn : c.nameFr}</Link>
            </React.Fragment>
          ))}
        </nav>
      )}
      <div className="listing-layout">
        <div className="listing-gallery">
          <div className="listing-main-photo">
            {photos.length ? <img src={photos[active].url} alt={t('listing.photo', { n: active + 1, total: photos.length })} /> : <span className="listing-card-nophoto">{t('listing.noPhoto')}</span>}
          </div>
          {photos.length > 1 && (
            <div className="listing-thumbs">
              {photos.map((p, i) => (
                <button key={p.id} type="button" className={i === active ? 'on' : ''} onClick={() => setActive(i)} aria-label={t('listing.photo', { n: i + 1, total: photos.length })} aria-pressed={i === active}>
                  <img src={p.url} alt="" loading="lazy" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="listing-info">
          <p className="listing-card-cat">{categoryName(listing.category, lang)}{listing.featured && <span className="featured-badge">★ {t('product.featuredBadge')}</span>}</p>
          <h1>{listing.title}</h1>
          <TutorialSlot place="site.listing" />
          <p className="listing-price">{formatPrice(listing, lang, t)}{listing.priceNegotiable && <span className="negotiable-badge">{t('wizard.negotiableShort')}</span>}</p>
          <dl className="listing-keyfacts">
            {listing.incoterm && <><dt>{t('product.fields.incoterm')}</dt><dd>{incotermLabel(listing.incoterm, t)}</dd></>}
            {listing.moq !== null && listing.moq !== undefined && <><dt>{t('product.fields.moq')}</dt><dd>{formatAmount(listing.moq, listing.moqUnit, lang, t)}</dd></>}
            {qty && <><dt>{t('listing.quantity')}</dt><dd>{qty}</dd></>}
            {listing.deliveryTime && <><dt>{t('product.fields.deliveryTime')}</dt><dd>{listing.deliveryTime}</dd></>}
          </dl>
          {listing.certifications?.length > 0 && (
            <ul className="chip-list listing-certs" aria-label={t('wizard.certifications')}>
              {listing.certifications.map((c) => <li key={c} className="chip">✓ {c === 'SGS' ? t('wizard.sgs') : c === 'Mill test certificate' ? t('wizard.mtc') : c}</li>)}
            </ul>
          )}
          {(listing.brochure || listing.videoUrl) && (
            <p className="listing-files">
              {listing.brochure && <a className="btn btn-ghost btn-sm" href={listing.brochure.url} target="_blank" rel="noreferrer">📄 {t('listing.brochure')}</a>}
              {listing.videoUrl && !embedUrl(listing.videoUrl) && <a className="btn btn-ghost btn-sm" href={listing.videoUrl} target="_blank" rel="noreferrer noopener">▶ {t('listing.video')}</a>}
            </p>
          )}
          <p className="muted small">{t('listing.published', { date: formatDate(listing.publishedAt, lang) })}</p>

          <section className="card seller-box">
            <p className="card-title">{t('listing.seller')}</p>
            <div className="seller-row">
              {listing.seller.logoUrl ? <img className="seller-logo" src={listing.seller.logoUrl} alt="" /> : <span className="seller-logo seller-logo-empty" aria-hidden="true">{listing.seller.companyName.slice(0, 1)}</span>}
              <div>
                <p className="strong">{listing.seller.companyName}</p>
                {place && <p className="muted small">{place}</p>}
              </div>
            </div>
            {/* The team member who added the product (migration 031). */}
            {listing.contact && (
              <div className="seller-row listing-contact">
                <Avatar name={listing.contact.name} photoUrl={listing.contact.photoUrl} size={44} />
                <div>
                  <p className="muted small" style={{ margin: 0 }}>{t('listing.contactPerson')}</p>
                  <p className="strong" style={{ margin: 0 }}>{listing.contact.name}</p>
                  {listing.contact.title && <p className="muted small" style={{ margin: 0 }}>{listing.contact.title}</p>}
                </div>
              </div>
            )}
            <Link to={`/sellers/${listing.seller.slug}`}>{t('listing.sellerProfile')} →</Link>
            <div className="seller-chat"><StartChatButton listingId={listing.id} ownerSlug={listing.seller.slug} className="btn btn-primary btn-block" /></div>
            {listing.whatsapp && !isOwn && (
              <div className="seller-chat"><WhatsAppButton number={listing.whatsapp} className="btn-block" label={t('wa.askProduct')}
                text={t('wa.productMessage', { name: listing.title, url: `${window.location.origin}/marketplace/${listing.id}` })} /></div>
            )}
          </section>
        </div>
      </div>

      <div className="listing-lower">
        <section>
          <h2>{t('listing.description')}</h2>
          {/* Sanitized on the server: only p, br, strong, em, ul, ol, li. */}
          <div className="rich-text listing-description" dangerouslySetInnerHTML={{ __html: listing.descriptionHtml }} />
          {embedUrl(listing.videoUrl) && (
            <div className="listing-video">
              <iframe src={embedUrl(listing.videoUrl)} title={t('listing.video')} loading="lazy" allow="encrypted-media; picture-in-picture" allowFullScreen referrerPolicy="strict-origin-when-cross-origin" />
            </div>
          )}
          <ProductFacts listing={listing} />
        </section>
        <section className="card" id="contact-seller">
          <h2>{t('listing.contactTitle')}</h2>
          {isOwn ? <p className="muted">{t('listing.ownListing')}</p> : (
            <>
              <p className="muted">{t('listing.contactLead')}</p>
              <ContactSellerForm listingId={listing.id} />
            </>
          )}
        </section>
      </div>
    </div>
  );
}
